import React, { useState } from "react";
import MobileModuleHeader from "../../components/MobileModuleHeader";
import { useAppDispatch, useAppSelector, useDebounce } from "../../app/hooks";
import { selectLabels } from "../../slices/language";
import { CircularProgress } from "@mui/material";
import LogCard from "../../components/LogCard";
import { selectUser, selectUserRole } from "../../slices/user";
import { selectToken } from "../../slices/token";
import { Context } from "../../context/ContextProvider/ContextProvider";
import { Dayjs } from "dayjs";
import api from "../../api";
import { AccessData } from "../../api/models/access-model";
import { useNavigate } from "react-router-dom";
import { showError } from "../../utils/helpers";

const MobLogbook = () => {
  const labels = useAppSelector(selectLabels);
  const user = useAppSelector(selectUser);
  const userRole = useAppSelector(selectUserRole);
  const isGuard = userRole === "guard";
  const token = useAppSelector(selectToken);
  const propertyId = user?.property?.id;
  const unitName = user?.unit?.name;
  const logbookUnitFilter = userRole === "host" || userRole === "secondary_host" ? unitName : undefined;
  const logbookVisitedUserFilter =
    userRole === "secondary_host" ? user?.name : undefined;
  const navigate = useNavigate();
  const missingLogbookScope =
    ((userRole === "host" || userRole === "secondary_host") && !unitName) ||
    (userRole === "secondary_host" && !user?.name);
  const dispatch = useAppDispatch();

  const { selectedAccessPointGuard } = React.useContext(Context);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [searchFilter, setSearchFilter] = useState<string>("");
  const [selectedAccessPoint, setSelectedAccessPoint] =
    React.useState<string>("0");
  const [selectedState, setSelectedState] = React.useState<
    "" | "entry" | "exit"
  >("");
  const [selectedDateFilter, setSelectedDateFilter] =
    React.useState<string>("all_dates");
  const [dateFilter, setDateFilter] = React.useState<Dayjs | null>(null);
  const [page, setPage] = React.useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [totalAccess, setTotalAccess] = React.useState<number>(0);
  const [accesses, setAccesses] = React.useState<AccessData[] | null>(null);

  const debouncedSearchFilter = useDebounce(searchFilter, 300);
  const logbookRequestId = React.useRef(0);

  const fixedPage = React.useMemo(() => {
    return page + 1;
  }, [page]);

  const accessPointsArray = user?.access_points
    ? user.access_points.map((item) => {
        return {
          label: item?.name,
          value: `${item.id}`,
        };
      })
    : [];

  accessPointsArray.unshift({
    label: labels.all_access_points,
    value: "0",
  });

  const getLogbookData = React.useCallback(
    async (debouncedText: string) => {
      const requestId = ++logbookRequestId.current;
      const guardPointId = Number(selectedAccessPointGuard);
      if (missingLogbookScope || !token || !propertyId || (isGuard && (!Number.isInteger(guardPointId) || guardPointId <= 0))) {
        setAccesses(null);
        setTotalAccess(0);
        setIsLoading(false);
        return;
      }
      if (token && propertyId) {
        let currentAccessPoint;
        if (!isGuard) {
          if (selectedAccessPoint === "0") {
            currentAccessPoint = undefined;
          } else {
            currentAccessPoint = Number(selectedAccessPoint);
          }
        } else {
          if (!selectedAccessPointGuard) {
            currentAccessPoint = undefined;
          } else {
            currentAccessPoint = Number(selectedAccessPointGuard);
          }
        }
        try {
          setIsLoading(true);
          const response = await api.getAccessesRequest(
            propertyId,
            "createdAt",
            fixedPage,
            rowsPerPage,
            currentAccessPoint,
            debouncedText,
            logbookUnitFilter,
            undefined,
            undefined,
            selectedState,
            logbookVisitedUserFilter,
          );
          if (requestId !== logbookRequestId.current) return;
          if (response) {
            setAccesses(response?.data);
            setTotalAccess(response.meta.pagination.total);
          }
        } catch (error: any) {
          if (requestId !== logbookRequestId.current) return;
          console.error(error);
          const errorMessage = error?.response?.data?.error?.message;
          showError(
            dispatch,
            errorMessage ? errorMessage : labels.generic_error
          );
        } finally {
          if (requestId === logbookRequestId.current) setIsLoading(false);
        }
      }
    },
    [
      missingLogbookScope,
      dispatch,
      fixedPage,
      isGuard,
      labels.generic_error,
      logbookUnitFilter,
      logbookVisitedUserFilter,
      propertyId,
      rowsPerPage,
      selectedAccessPoint,
      selectedAccessPointGuard,
      selectedState,
      token,
    ]
  );

  React.useEffect(() => {
    getLogbookData(debouncedSearchFilter);
    return () => { logbookRequestId.current += 1; };
  }, [debouncedSearchFilter, getLogbookData]);

  return (
    <div className="mobile-module-body" style={{ position: "relative" }}>
      <div
        style={{
          zIndex: 99,
          position: "relative",
        }}
      >
        <MobileModuleHeader
          title={labels.logbook}
          titleIcon={"fa6-solid:book"}
          searchValue={searchFilter}
          setSearchValue={setSearchFilter}
          hasSearch={true}
          hasFilters={false}
          isDowloadable={false}
          hasNewButton={false}
        />
      </div>
      <div
        className={"mobile-module-body-no-filters-content"}
        style={{ zIndex: 10, position: "relative" }}
      >
        {isLoading ? (
          <div className="w-100 h-100 flex justify-content-center align-items-center">
            <CircularProgress />
          </div>
        ) : (
          <>
            {accesses &&
              accesses.length > 0 &&
              accesses.map((item, index) => (
                <LogCard
                  key={`${item?.id}-${index}`}
                  log={item}
                  style={{
                    marginBottom: accesses.length - 1 === index ? 0 : 8,
                  }}
                  onPress={() => navigate(`/access-details/${item?.documentId}`)}
                />
              ))}
          </>
        )}
      </div>
    </div>
  );
};

export default MobLogbook;
